// Примітиви інформаційної панелі кабінету — той самий патерн, що вже вживає
// EMS Box у секції «ФЕС · сонячна генерація»:
//
//   [ SectionHead ]
//   [ візуалізація (гейдж/схема) ][ колонка метрик ]
//
// Метрика — плитка іконки 42×42 на --surface-2, під нею підпис 11.5
// (--text-dim) і число 23/600 шрифтом font-display. Розміри, відступи й
// типографіка взяті з наявної реалізації, щоб дві секції виглядали як одна
// система, а не як дві схожі.
//
// Винесено сюди, а не імпортовано з pages/ems/page.tsx, бо там це локальні
// функції всередині чужого файлу. Коли власник /ems забажає, він зможе перейти
// на ці ж компоненти — контракт збігається один в один.
import type { CSSProperties } from 'react';
import type { LucideIcon } from 'lucide-react';

/** Заголовок секції: іконка · назва · уточнення. */
export function SectionHead({ Icon, title, hint }: { Icon: LucideIcon; title: string; hint?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 2px 12px' }}>
      <Icon size={17} strokeWidth={1.9} style={{ color: 'var(--text)' }} />
      <span className="font-display" style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{title}</span>
      {hint && <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>· {hint}</span>}
    </div>
  );
}

export interface PanelMetric {
  Icon: LucideIcon;
  /** Підпис показника — видимий, як у EMS Box. */
  label: string;
  value: string;
  /** Одиниця виміру окремо, дрібнішим — щоб число лишалось головним. */
  unit?: string;
  /** Колір числа: за замовчуванням основний текст. */
  color?: string;
  title?: string;
}

/**
 * Набір метрик поруч із візуалізацією.
 * `layout="column"` — вертикальна колонка (як у EMS Box);
 * `layout="grid"` — сітка 2×2, коли поруч ще й тематичний малюнок і колонка
 * була б надто високою;
 * `layout="row"` — усі показники в один рядок поруч один з одним (компактно,
 * не розпирає сусідню колонку — саме тому не пересуває сусідній елемент,
 * напр. гейдж, убік).
 */
export function MetricStack({ items, minWidth = 156, layout = 'column', valueSize = 15, gap, fill }: {
  items: PanelMetric[]; minWidth?: number; layout?: 'column' | 'grid' | 'row';
  /** Розтягнути колонку на висоту рядка, щоб `space-evenly` реально розганяв
   *  показники. У EMS Box так виходить саме собою (батьківський flex без
   *  `align-items`, тобто stretch); там, де батько центрує вміст
   *  (`items-center`), без цього прапорця колонка стискається до вмісту й
   *  плитки іконок злипаються. */
  fill?: boolean;
  /** Кегль значення: 15 — вторинний показник поруч із гейджем (за замовчуванням),
   *  23 — головна колонка метрик, як у EMS Box. */
  valueSize?: number;
  /** Власний проміжок між рядками; у EMS Box його немає — розподіляє space-evenly. */
  gap?: number;
}) {
  const gridStyle: CSSProperties =
    layout === 'row' ? { display: 'grid', gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`, gap: 16 }
    : layout === 'grid' ? { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14, minWidth, flex: '1 1 260px' }
    : { display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', minWidth, flexShrink: 0, gap: gap ?? 12, alignSelf: fill ? 'stretch' : undefined };
  return (
    <div style={gridStyle}>
      {items.map((m) => (
        <div key={m.label} title={m.title ?? m.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12, background: 'var(--surface-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <m.Icon size={19} strokeWidth={1.75} style={{ color: 'var(--text)' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>{m.label}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <span
                className="font-display tabular-nums"
                // Значення — ВТОРИННИЙ показник: головне число секції стоїть
                // у гейджі, і два великі кеглі поруч конкурували б між собою.
                style={{ fontSize: valueSize, fontWeight: 600, color: m.color ?? 'var(--text)', lineHeight: valueSize >= 20 ? 1 : 1.2 }}
              >
                {m.value}
              </span>
              {m.unit && <span style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>{m.unit}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
